const FULL_GAME_PERIODS = new Set(["", "FULL_GAME", "FULL GAME", "GAME"]);

const normalizeMarket = (value) => String(value ?? "")
  .trim()
  .toLowerCase()
  .replace(/points/g, "pts")
  .replace(/rebounds/g, "rebs")
  .replace(/assists/g, "asts")
  .replace(/blocks/g, "blks")
  .replace(/steals/g, "stls")
  .replace(/[^a-z0-9]/g, "");

const MARKET_ALIASES = Object.freeze({
  points: new Set(["points", "pts"]),
  rebounds: new Set(["rebounds", "rebs", "reb"]),
  assists: new Set(["assists", "asts", "ast"]),
  blocks: new Set(["blocks", "blks", "blk", "blockedshots"]),
  steals: new Set(["steals", "stls", "stl"]),
  pointsRebounds: new Set(["pr", "ptsrebs"]),
  pointsAssists: new Set(["pa", "ptsasts"]),
  reboundsAssists: new Set(["ra", "rebsasts"]),
  pra: new Set(["pra", "ptsrebsasts"]),
  blocksSteals: new Set(["blksstls", "blkstl"]),
});

const STAT_FIELDS = Object.freeze({
  points: ["Points", "PTS", "points", "pts"],
  rebounds: ["Rebounds", "REB", "TRB", "rebounds", "reb"],
  assists: ["Assists", "AST", "assists", "ast"],
  blocks: ["Blocks", "BLK", "Blocked Shots", "blocks", "blk"],
  steals: ["Steals", "STL", "steals", "stl"],
});

const EXPLICIT_COMBINED_FIELDS = Object.freeze({
  pointsRebounds: ["Points + Rebounds", "Pts+Rebs", "PTS+REB", "PTS+REBS", "P+R", "PR"],
  pointsAssists: ["Points + Assists", "Pts+Asts", "Pts+Ast", "PTS+AST", "PTS+ASTS", "P+A", "PA"],
  reboundsAssists: ["Rebounds + Assists", "Rebs+Asts", "Reb+Ast", "REB+AST", "REBS+ASTS", "R+A", "RA"],
  pra: ["Points + Rebounds + Assists", "Pts+Rebs+Asts", "PTS+REB+AST", "PTS+REBS+ASTS", "P+R+A", "PRA"],
  blocksSteals: ["Blocks + Steals", "Blks+Stls", "BLK+STL", "BLKS+STLS"],
});

const DERIVED_COMPONENTS = Object.freeze({
  pointsRebounds: ["points", "rebounds"],
  pointsAssists: ["points", "assists"],
  reboundsAssists: ["rebounds", "assists"],
  pra: ["points", "rebounds", "assists"],
  blocksSteals: ["blocks", "steals"],
});

const finiteValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readField = (row, fields) => {
  for (const field of fields || []) {
    const value = finiteValue(row?.[field]);
    if (value !== null) return value;
  }
  return null;
};

export function canonicalNbaHistoryMarket(market) {
  const normalized = normalizeMarket(market);
  for (const [canonical, aliases] of Object.entries(MARKET_ALIASES)) {
    if (aliases.has(normalized)) return canonical;
  }
  return null;
}

/**
 * Resolve one NBA market from one game row. Combined markets never borrow
 * components from another row, and are derived only when every component is
 * present. A source-supplied explicit combined zero remains a valid zero.
 */
export function resolveNbaHistoricalStat(row, market, { period = "FULL_GAME" } = {}) {
  if (!row || !FULL_GAME_PERIODS.has(String(period ?? "").trim().toUpperCase())) return null;
  const canonical = canonicalNbaHistoryMarket(market);
  if (!canonical) return null;

  if (STAT_FIELDS[canonical]) return readField(row, STAT_FIELDS[canonical]);

  const explicit = readField(row, EXPLICIT_COMBINED_FIELDS[canonical]);
  if (explicit !== null) return explicit;

  const components = (DERIVED_COMPONENTS[canonical] || []).map((component) => (
    readField(row, STAT_FIELDS[component])
  ));
  return components.length && components.every((value) => value !== null)
    ? components.reduce((total, value) => total + value, 0)
    : null;
}

export const NBA_HISTORY_SOURCE_FIELDS = Object.freeze({
  points: STAT_FIELDS.points,
  rebounds: STAT_FIELDS.rebounds,
  assists: STAT_FIELDS.assists,
  blocks: STAT_FIELDS.blocks,
  steals: STAT_FIELDS.steals,
});
